import { faArrowRightArrowLeft, faCartShopping, faCircleCheck, faPenToSquare, faTrash, faWarning } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Stack, Grid, Button, Tooltip, Box, Popover, Typography } from '@vapor/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiCIcon } from 'src/app/components/icons/icon'
import { CartEditableQuote, ReservationCartProps } from '../../form/reservation-new.type'
import { isCartEditableQuote, isCartQuote } from '../../form/reservation-new.utils'
import { HICTitle } from 'src/app/components/typography/title'
import { ReservationCartNew } from './reservation-cart-new'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'

export default function ReservationCart(props: ReservationCartProps) {
  const { t } = useTranslation()
  const { sm } = useBreakpoints()

  const [showWarningIcon, setShowWarningIcon] = useState(false)

  useEffect(() => {
    const initialRow = props.rows.find((r) => r.isFirstInsertion)
    if (initialRow) {
      const incompatibleReservation = props.rows.find((r) => r.rate.id !== initialRow.rate.id || r.chargeCategory.id !== initialRow.chargeCategory.id)
      setShowWarningIcon(incompatibleReservation !== undefined)
    }
  }, [JSON.stringify(props.rows)])

  //#region delete popover
  const [deletePopoverAnchorEl, setDeletePopoverAnchorEl] = useState<HTMLButtonElement | null>(null)
  const handleShowDeletePopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDeletePopoverAnchorEl(event.currentTarget)
  }
  const handleCloseDeletePopover = () => {
    setDeletePopoverAnchorEl(null)
  }
  const openDeletePopover = Boolean(deletePopoverAnchorEl)
  //#endregion

  return (
    <>
      <Stack direction="column" spacing={2}>
        <Stack direction="row" alignItems="center" width="100%">
          <Grid display="flex" flexDirection="column" justifyContent="start" mr={1} width="100%">
            <HICTitle //
              title={props.title}
              titleEnd={
                showWarningIcon ? (
                  <Grid display="flex" flexDirection="column" justifyContent="center" ml={1}>
                    <Tooltip title={t('pms.booking.reservation_new.incompatible_rate_or_charge_category')}>
                      <span>
                        <FontAwesomeIcon icon={faWarning} color="orange" style={{ width: 16, height: 16, display: 'flex' }} />
                      </span>
                    </Tooltip>
                  </Grid>
                ) : undefined
              }
              faIcon={faCartShopping}
              removeMargin
              sx={{ alignContent: 'start' }}
            />
          </Grid>

          {props.isQuote !== undefined && (
            <Stack direction="row" spacing={1} width="100%" justifyContent="flex-end" alignItems="center" py={2}>
              <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                <>
                  {props.isQuote.asReservationButton.show && (
                    <>
                      {isCartEditableQuote(props.isQuote) && (
                        <Button
                          size="small"
                          onClick={() => props.isQuote?.asReservationButton.onClick()}
                          startIcon={
                            <HiCIcon>
                              <FontAwesomeIcon icon={faArrowRightArrowLeft} height={'14px'} width={'16px'} />
                            </HiCIcon>
                          }
                          sx={{ minWidth: !sm ? '32px !important' : undefined, '& .MuiButton-startIcon': !sm ? { marginRight: '0px' } : undefined }}
                        >
                          {sm && <u>{t('pms.quote.form.as_reservation')}</u>}
                        </Button>
                      )}
                      {isCartQuote(props.isQuote) && (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => props.isQuote?.asReservationButton.onClick()}
                            sx={{ minWidth: !sm ? '32px !important' : undefined, '& .MuiButton-startIcon': !sm ? { marginRight: '0px' } : undefined }}
                            startIcon={
                              <HiCIcon>
                                <FontAwesomeIcon icon={faCircleCheck} />
                              </HiCIcon>
                            }
                          >
                            <p>{t('pms.quote.proposal.as_reservation')}</p>
                          </Button>
                        </>
                      )}
                    </>
                  )}
                  {props.isQuote.asReservationButton.showResourcesUnavailableError && (
                    <Box paddingX="6px" paddingY="2px" style={{ backgroundColor: 'var(--MaximumRed)', borderRadius: '25px' }}>
                      <Typography color="white" variant="body">
                        {t('pms.booking.reservation_new.cart.resources_unavailable')}
                      </Typography>
                    </Box>
                  )}
                </>
              </Stack>

              {isCartEditableQuote(props.isQuote) && !(props.isQuote as CartEditableQuote).hideEditButton && (
                <Button
                  size="small"
                  startIcon={
                    <HiCIcon>
                      <FontAwesomeIcon color="red" icon={faPenToSquare} height={'16px'} width={'16px'} />
                    </HiCIcon>
                  }
                  onClick={() => {
                    const isQuote = props.isQuote
                    if (isQuote && isCartEditableQuote(isQuote)) {
                      isQuote.onEditQuoteClick(isQuote.quoteId)
                    }
                  }}
                  sx={{ minWidth: !sm ? '32px !important' : undefined, '& .MuiButton-startIcon': !sm ? { marginRight: '0px' } : undefined }}
                >
                  {sm && <u>{t('commons.edit')}</u>}
                </Button>
              )}

              {isCartEditableQuote(props.isQuote) && (
                <Button
                  size="small"
                  startIcon={<FontAwesomeIcon icon={faTrash} height={'16px'} width={'14px'} />}
                  color="error"
                  onClick={handleShowDeletePopover}
                  sx={{ minWidth: !sm ? '32px !important' : undefined, '& .MuiButton-startIcon': !sm ? { marginRight: '0px' } : undefined }}
                >
                  {sm && <u>{t('commons.delete')}</u>}
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </Stack>

      <Stack direction="row" sx={{ backgroundColor: 'var(--AzureishWhite)', width: '100%' }}>
        <ReservationCartNew {...props} />
      </Stack>

      <Popover
        open={openDeletePopover}
        anchorEl={deletePopoverAnchorEl}
        onClose={handleCloseDeletePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Stack direction="column" spacing={0} justifyContent="center" alignItems="center" m={1}>
          <Typography variant="body" pb={1} width="100%">
            <b>{t('pms.booking.reservation_new.cart.confirm_quote_deletion_message')}</b>
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" width="100%">
            <Button size="small" variant="outlined" onClick={handleCloseDeletePopover} sx={{ minWidth: '32px !important', width: '40px' }}>
              {t('commons.no')}
            </Button>
            <Button
              color="error"
              size="small"
              variant="contained"
              sx={{ minWidth: '32px !important', width: '40px' }}
              onClick={() => {
                const isQuote = props.isQuote
                if (isCartEditableQuote(isQuote)) {
                  isQuote?.onRemoveQuoteClick(isQuote.quoteId)
                }
                handleCloseDeletePopover()
              }}
            >
              {t('commons.yes')}
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  )
}
